import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LeapV2ProgressReport } from '../models/leap-v2-progress-report.model';
import { LeapV2UploadedFile } from '../models/leap-v2-student.model';
import { LeapV2ProgressReportService } from '../services/leap-v2-progress-report.service';
import { LeapV2EvidenceService } from '../services/leap-v2-evidence.service';
import { LeapV2EvidenceUploadComponent } from '../leap-v2-evidence-upload/leap-v2-evidence-upload.component';
import { LeapV2DebugDialogComponent } from '../dialogs/leap-v2-debug-dialog/leap-v2-debug-dialog.component';
import { TutorReportsHelperService } from '../../../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { FeatureHelperService } from '../../../../_helpers/feature-helper/feature-helper.service';
import { ErrorModalHelperService } from '../../../../_helpers/error-modal-helper/error-modal-helper.service';
import { DynamicConfirmModalComponent } from '../../../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { SubmissionHistoryDialog } from '../../../../shared/submission-history-dialog';
import { SEMH_LABELS, SEMH_MIN, SEMH_MAX } from '../models/leap-v2-semh';
import { hasRole } from '../../../../_helpers/can-use/current-user-roles';
import { returnToPreviousPage } from '../../../../_helpers/navigation/return-to-previous-page';
import { PlacementService } from '../../../../_services/placementservice';
import { PlacementData } from '../../../../_models';
import { PlacementMaintenance } from '../../../../_models/maintenance/placement-maintenance.model';

@Component({
  selector: 'leap-v2-progress-report',
  templateUrl: './leap-v2-progress-report.component.html',
  styleUrls: ['./leap-v2-progress-report.component.css'],
  standalone: false,
})
export class LeapV2ProgressReportComponent implements OnInit, OnDestroy {
  // The booking summary header; AppNewBaseComponent picks this template up for its utility bar.
  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;

  // Populated by the load call in ngOnInit; the template guards on this being set.
  report: LeapV2ProgressReport;

  // Files already stored for this report, as the server last returned them (39903: the screen never listed
  // them, so a submitted or reopened report looked empty). report.evidence is these plus the files picked since.
  savedEvidence: LeapV2UploadedFile[] = [];
  @ViewChild(LeapV2EvidenceUploadComponent) evidenceUpload: LeapV2EvidenceUploadComponent;
  // The files picked since load, as the upload control last emitted them.
  private picked: LeapV2UploadedFile[] = [];

  placementData: PlacementData;
  placementMaintenance: PlacementMaintenance;
  private unsubscribe: Subject<void> = new Subject<void>();
  // Picked files the API has already stored for this report; see withoutUploadedFiles.
  private readonly uploadedFiles = new WeakSet<File>();

  // Debug shows the raw model, so admins only.
  readonly canDebug = hasRole('ADMIN');
  readonly maxChars = 2000;
  readonly percentTicks = ['0%', '25%', '50%', '75%', '100%'];
  readonly semhLabels = SEMH_LABELS;
  readonly semhMin = SEMH_MIN;
  readonly semhMax = SEMH_MAX;

  placementId: number;
  studentId: number;

  constructor(
    private cdr: ChangeDetectorRef,
    private progressReportService: LeapV2ProgressReportService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private featureHelperService: FeatureHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private tutorReportsHelperService: TutorReportsHelperService,
    private placementService: PlacementService,
    private evidenceService: LeapV2EvidenceService
  ) { }

  /** Opens a stored file through the API (its link is api/Evidence/{id}, not a blob URL). */
  openEvidence(event: Event, file: LeapV2UploadedFile): void {
    event.preventDefault();
    this.evidenceService.open(file);
  }

  openDebugDialog(): void {
    this.dialog.open(LeapV2DebugDialogComponent, {
      width: '600px',
      data: { title: 'progress report model', model: this.report },
    });
  }

  ngOnInit(): void {
    // Ids come from the route (/progress-report/:placementId/:studentId) so the screen is deep-linkable.
    this.placementId = Number(this.route.snapshot.paramMap.get('placementId'));
    this.studentId = Number(this.route.snapshot.paramMap.get('studentId'));
    this.tutorReportsHelperService.ensureContext(this.placementId);
    this.loadPlacementHeader();

    // ?reportId=<id> opens that specific report (from the "View progress reports" tile list);
    // no reportId => a blank, save-ready report for a brand-new entry.
    const reportId = Number(this.route.snapshot.queryParamMap.get('reportId'));
    if (reportId > 0) {
      // A report that is not found or not the user's reads as one plain message, not the support modal (40973).
      this.progressReportService.getProgressReportById(reportId, true).subscribe({
        next: report => {
          this.report = report;
          this.savedEvidence = savedEvidenceOf(report);
          this.cdr.detectChanges();
        },
        error: error => this.errorModalHelper.show(error.message, error)
      });
    } else {
      this.report = this.progressReportService.newProgressReport(this.placementId, this.studentId);
    }
  }

  /** Submit is gated until both required narrative fields have content. */
  get canSubmit(): boolean {
    return !!this.report
      && this.report.progressSummary.trim().length > 0
      && this.report.nextPlan.trim().length > 0;
  }

  /**
   * A submitted (or approved) report is locked: every control is read-only/disabled and it can't be
   * saved or submitted again until a consultant unlocks it. statusId 2 = Submitted, 3 = Approved.
   */
  get isLocked(): boolean {
    return !!this.report
      && (this.report.approved === true
        || this.report.status === 'Submitted'
        || this.report.statusId === 2
        || this.report.statusId === 3);
  }

  // Submitted is the only state Accept/Reject apply to: the tutor is done and QA has not signed off yet.
  get reportSubmitted(): boolean {
    return !!this.report && this.report.status === 'Submitted';
  }

  // Approved is terminal for sign-off — no Save/Submit and no Accept/Reject. Unlock is the way back.
  get reportFinalised(): boolean {
    return !!this.report && this.report.status === 'Approved';
  }

  // The progress report has its own approve/reject features — deliberately not the session report's
  // TutorReports* pair. Same gating as the progress rows on the QA approval grid; the API enforces it
  // too, this only hides UI.
  //
  // Do NOT gate this on `isStaff`. That flag means "holds the STAFF role code", and STAFF is the
  // TUTOR role here — using it shows Accept to tutors and hides it from admins.
  get canApprove(): boolean {
    return this.featureHelperService.hasFeature('ProgressReportsApprove');
  }

  get canReject(): boolean {
    return this.featureHelperService.hasFeature('ProgressReportsReject');
  }

  // Drives the footer branch: the Accept/Reject row shows when the user can do either. A tutor holds
  // neither feature and so keeps the Save/Submit footer they see today.
  get canAuthorise(): boolean {
    return this.canApprove || this.canReject;
  }

  /**
   * The stored files the screen lists. One the tutor has attached again under the same name is left out: the
   * save stores the new copy in its place, so it is listed once, in the picker.
   */
  get shownSavedEvidence(): LeapV2UploadedFile[] {
    return this.savedEvidence.filter(s => !this.picked.some(p => sameName(p, s)));
  }

  // The upload control emits only the files picked since load, so keep the stored ones alongside them. They go
  // back to the API by url and are not stored again. The API stores exactly this list (40454): a stored file it
  // no longer contains is removed.
  onEvidenceChanged(files: LeapV2UploadedFile[]): void {
    this.picked = files ?? [];
    this.report.evidence = [...this.shownSavedEvidence, ...this.picked];
  }

  /**
   * Removes a stored file from a draft once the tutor confirms (40454). Only the screen changes here: the next
   * save sends the list without it, and the API removes it then. A submitted or approved report keeps its files.
   */
  removeSavedEvidence(file: LeapV2UploadedFile): void {
    if (this.isLocked) return;
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: `Remove ${file.name} from the evidence of work?`, confirmLabel: 'Remove' },
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.savedEvidence = this.savedEvidence.filter(s => s !== file);
        this.onEvidenceChanged(this.picked);
        this.cdr.detectChanges();
      });
  }

  saveProgressReport(): void {
    if (this.isLocked) return;
    const sent = this.withoutUploadedFiles();
    // Keep the id the API returns so repeat saves update the same row instead of inserting new ones, then
    // re-read, as the ILP does, so the files just uploaded are listed from the server rather than left in the
    // picker, where an X would hide a file that stays stored.
    this.progressReportService.saveProgressReport(sent).subscribe({
      next: id => {
        this.markUploaded(sent);
        this.report.id = id;
        this.reload();
      },
      // A refused save (422, e.g. a file that could not be read) is not shown by the error interceptor.
      error: (error) => this.errorModalHelper.show(error.message, error),
    });
  }

  submitProgressReport(): void {
    if (this.isLocked || !this.canSubmit) return;
    const sent = this.withoutUploadedFiles();
    this.progressReportService.submitProgressReport(sent).subscribe({
      next: id => {
        this.markUploaded(sent);
        // Re-read rather than assume: the status submit lands on is brand-conditional — FES goes to
        // Submitted for QA, everyone else auto-approves — so hard-coding Approved here would label an
        // FES report wrongly. Both statuses lock the screen, so only the badge was ever at stake.
        this.report.id = id;
        this.reload();
      },
      error: (error) => this.errorModalHelper.show(error.message, error),
    });
  }

  /**
   * Accept — QA sign-off, Submitted to Approved. Same endpoint the QA approval grid calls; the report
   * becomes visible to the client.
   */
  acceptProgressReport(): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: 'Approve this progress report? It becomes visible to the client.' },
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;

        this.progressReportService.authoriseProgressReport(this.report.id).subscribe({
          next: () => this.reload(),
          error: (error) => this.errorModalHelper.show(error.message, error),
        });
      });
  }

  /**
   * Reject — sends the report back to the tutor with a mandatory reason, Submitted to Rejected.
   * Rejected is not terminal: the screen unlocks so the tutor can correct and resubmit.
   *
   * Reuses the legacy SubmissionHistoryDialog (20–200 chars). Its Cancel and close-X both emit `true`
   * from afterClosed(), so only the onConfirmed emit counts as a confirmation.
   */
  rejectProgressReport(): void {
    const dialogRef = this.dialog.open(SubmissionHistoryDialog, {
      panelClass: 'my-dialog',
      data: { title: 'Reject progress report', label: 'Reason for rejection (emailed to the tutor when rejection emails are switched on)' },
    });
    const confirmed = dialogRef.componentInstance.onConfirmed.subscribe((reason: string) => {
      dialogRef.close();
      this.progressReportService.rejectProgressReport(this.report.id, reason).subscribe({
        next: () => this.reload(),
        error: (error) => this.errorModalHelper.show(error.message, error),
      });
    });
    dialogRef.afterClosed().subscribe(() => confirmed.unsubscribe());
  }

  unlockProgressReport(): void {
    this.progressReportService.unlockProgressReport(this.report.id, this.studentId).subscribe(() => {
      this.report.approved = false;
      this.report.status = 'Draft';
      this.report.statusId = 1; // Saved — back to editable so the lock lifts.
      this.cdr.detectChanges();
    });
  }

  backToProfile(): void {
    returnToPreviousPage(this.router, this.placementId);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /** Feeds the booking summary header from the shared placement context, as View Sessions does. */
  private loadPlacementHeader(): void {
    this.tutorReportsHelperService.placementData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => this.placementData = data);
    this.tutorReportsHelperService.placementMaintenance$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(maintenance => this.placementMaintenance = maintenance);

    this.tutorReportsHelperService.loadPlacementHeader(this.placementId, this.placementService,
      error => this.errorModalHelper.show(error.message, error));
  }

  /**
   * The report to send, with files this screen has already uploaded reduced to their name. The evidence
   * picker keeps every picked File and re-emits them on each change, so sending them again would store
   * a second copy on every save or submit. The API only stores files that carry content.
   */
  private withoutUploadedFiles(): LeapV2ProgressReport {
    return {
      ...this.report,
      evidence: (this.report.evidence ?? []).map(f =>
        f.file && this.uploadedFiles.has(f.file) ? { name: f.name, size: f.size, contentType: f.contentType } : f),
    };
  }

  private markUploaded(sent: LeapV2ProgressReport): void {
    sent.evidence.forEach(f => { if (f.file) this.uploadedFiles.add(f.file); });
  }

  /**
   * Re-reads the report so the status pill, the lock and the footer all come from the server record
   * rather than a guess. ngZone.run because the dialog callbacks these run from can land outside
   * Angular's zone.
   */
  private reload(): void {
    this.progressReportService.getProgressReportById(this.report.id).subscribe(saved => {
      this.ngZone.run(() => {
        this.report = saved;
        this.savedEvidence = savedEvidenceOf(saved);
        // The picked files are uploaded now and listed above; leaving them in the control would show them twice.
        this.picked = [];
        if (this.evidenceUpload) this.evidenceUpload.files = [];
        this.cdr.detectChanges();
      });
    });
  }
}

/** The report's stored evidence: the files the server returned with a link to open. */
function savedEvidenceOf(report: LeapV2ProgressReport): LeapV2UploadedFile[] {
  return (report?.evidence ?? []).filter(f => !!f.url);
}

/** Same file name, ignoring case, as the API matches them. */
function sameName(a: LeapV2UploadedFile, b: LeapV2UploadedFile): boolean {
  return (a?.name ?? '').toLowerCase() === (b?.name ?? '').toLowerCase();
}
